import React, { useMemo } from 'react';
import {
  AlertTriangle,
  Check,
  CircleDot,
  Eraser,
  Loader2,
  MousePointer2,
  Paintbrush,
  Redo2,
  Route,
  Undo2,
} from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { networkTypeKey } from './mapFeatures';

const copy = {
  fr: {
    ground: 'Sol', networks: 'Réseaux', obstacles: 'Obstacles',
    networkTitle: 'Réseaux de déplacement', networkDescription: 'Routes et liaisons sans effacer le terrain',
    conditionTitle: 'Obstacles', conditionDescription: 'Conditions ponctuelles qui modifient le passage',
    networkType: 'Réseau actif', conditionType: 'Obstacle actif', infrastructure: 'Infrastructure', connection: 'Liaison spéciale',
    navigate: 'Naviguer', draw: 'Tracer', place: 'Placer', erase: 'Effacer', visible: 'Afficher ce calque',
    networkHelp: 'Glissez sur la carte : chaque centre d’hexagone traversé prolonge le réseau. Une route peut traverser une forêt sans la remplacer.',
    conditionHelp: 'Cliquez un hexagone pour poser la condition. Les transports bloqués ou ralentis viennent du paquet d’univers.',
    segmentCount: 'segments tracés', conditionCount: 'hexagones affectés', undo: 'Annuler', redo: 'Rétablir',
    saved: 'Enregistré', saving: 'Enregistrement…', unsaved: 'Modifié',
    stale: 'La grille a changé : les anciens réseaux et obstacles ont été écartés.', empty: 'Aucun type déclaré par ce paquet.',
  },
  en: {
    ground: 'Ground', networks: 'Networks', obstacles: 'Obstacles',
    networkTitle: 'Travel networks', networkDescription: 'Roads and links without replacing terrain',
    conditionTitle: 'Obstacles', conditionDescription: 'Point conditions that alter traversal',
    networkType: 'Active network', conditionType: 'Active obstacle', infrastructure: 'Infrastructure', connection: 'Special link',
    navigate: 'Navigate', draw: 'Draw', place: 'Place', erase: 'Erase', visible: 'Show this layer',
    networkHelp: 'Drag across the map: every crossed hex center extends the network. A road can cross a forest without replacing it.',
    conditionHelp: 'Click a hex to place the condition. Transport blocking and slowdown come from the universe package.',
    segmentCount: 'segments drawn', conditionCount: 'affected hexes', undo: 'Undo', redo: 'Redo',
    saved: 'Saved', saving: 'Saving…', unsaved: 'Changed',
    stale: 'The grid changed: previous networks and obstacles were discarded.', empty: 'This package declares no type.',
  },
};

const localLabel = (item, language) => item?.label?.[language] || item?.label?.fr || item?.id || '—';

export function TravelLayerSwitcher({ language = 'fr', value, onChange }) {
  const text = copy[language] || copy.fr;
  return (
    <div className="grid grid-cols-3 gap-1 rounded-lg border bg-muted/35 p-1" aria-label={language === 'fr' ? 'Type de calque' : 'Layer type'}>
      {[
        ['ground', text.ground],
        ['network', text.networks],
        ['condition', text.obstacles],
      ].map(([id, label]) => (
        <Button key={id} type="button" size="sm" variant={value === id ? 'secondary' : 'ghost'} onClick={() => onChange(id)}>
          {label}
        </Button>
      ))}
    </div>
  );
}

export function TravelMapFeaturesPanel({
  language = 'fr',
  layer = 'network',
  infrastructure = [],
  connections = [],
  conditions = [],
  selectedNetworkType,
  onSelectedNetworkTypeChange,
  selectedConditionId,
  onSelectedConditionChange,
  interactionMode,
  onInteractionModeChange,
  networkSegmentCount = 0,
  conditionHexCount = 0,
  overlayEnabled = true,
  onOverlayEnabledChange,
  saveStatus = 'saved',
  stale = false,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
}) {
  const text = copy[language] || copy.fr;
  const isNetwork = layer === 'network';
  const status = {
    saved: { label: text.saved, icon: Check, className: 'text-emerald-600 dark:text-emerald-300' },
    saving: { label: text.saving, icon: Loader2, className: 'text-amber-600 dark:text-amber-300' },
    dirty: { label: text.unsaved, icon: CircleDot, className: 'text-muted-foreground' },
  }[saveStatus];
  const StatusIcon = status.icon;
  const networkOptions = useMemo(() => [
    ...infrastructure.map((item) => ({ ...item, catalog: 'infrastructure' })),
    ...connections.map((item) => ({ ...item, catalog: 'connection' })),
  ], [connections, infrastructure]);
  const selectedValue = isNetwork ? selectedNetworkType : selectedConditionId;
  const options = isNetwork ? networkOptions : conditions;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              {isNetwork ? <Route className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {isNetwork ? text.networkTitle : text.conditionTitle}
            </CardTitle>
            <CardDescription>{isNetwork ? text.networkDescription : text.conditionDescription}</CardDescription>
          </div>
          <Badge variant="outline" className={status.className}>
            <StatusIcon className={`mr-1 h-3 w-3 ${saveStatus === 'saving' ? 'animate-spin' : ''}`} />{status.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {stale && <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-200">{text.stale}</p>}

        <div className="flex items-center justify-between rounded-lg border bg-muted/20 px-3 py-2">
          <div>
            <p className="text-lg font-semibold tabular-nums">{isNetwork ? networkSegmentCount : conditionHexCount}</p>
            <p className="text-[10px] text-muted-foreground">{isNetwork ? text.segmentCount : text.conditionCount}</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor={`travel-${layer}-overlay`} className="text-xs">{text.visible}</Label>
            <Switch id={`travel-${layer}-overlay`} checked={overlayEnabled} onCheckedChange={onOverlayEnabledChange} />
          </div>
        </div>

        {options.length ? (
          <div className="space-y-2">
            <Label>{isNetwork ? text.networkType : text.conditionType}</Label>
            <Select value={selectedValue || ''} onValueChange={isNetwork ? onSelectedNetworkTypeChange : onSelectedConditionChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((item) => {
                  const value = isNetwork ? networkTypeKey(item.catalog, item.id) : item.id;
                  const category = item.catalog === 'connection' ? text.connection : text.infrastructure;
                  return <SelectItem key={value} value={value}>{localLabel(item, language)}{isNetwork ? ` · ${category}` : ''}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
        ) : <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">{text.empty}</p>}

        <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
          {[
            ['navigate', MousePointer2, text.navigate],
            [isNetwork ? 'network_draw' : 'condition_paint', Paintbrush, isNetwork ? text.draw : text.place],
            [isNetwork ? 'network_erase' : 'condition_erase', Eraser, text.erase],
          ].map(([mode, Icon, label]) => (
            <Button
              key={mode}
              type="button"
              size="sm"
              variant={interactionMode === mode ? 'secondary' : 'ghost'}
              className="h-auto min-w-0 flex-col gap-1 px-1 py-2 text-[10px]"
              disabled={mode !== 'navigate' && !options.length}
              onClick={() => onInteractionModeChange(mode)}
            >
              <Icon className="h-4 w-4" />{label}
            </Button>
          ))}
        </div>

        <p className="rounded-lg border border-dashed p-3 text-xs leading-relaxed text-muted-foreground">
          {isNetwork ? text.networkHelp : text.conditionHelp}
        </p>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" disabled={!canUndo} onClick={onUndo}><Undo2 />{text.undo}</Button>
          <Button variant="outline" size="sm" className="flex-1" disabled={!canRedo} onClick={onRedo}><Redo2 />{text.redo}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default TravelMapFeaturesPanel;
